import { Link } from "react-router-dom";
import {
  Sprout, Scissors, TreePalm, Layers, ShieldCheck, FlaskConical,
  Grid3x3, Compass, Droplets, Leaf, Lightbulb, Mountain, Droplet, Star, Phone, MapPin, Award, ShieldHalf, Clock,
  ArrowRight,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { BRAND, SERVICES, TESTIMONIALS } from "../lib/brand";

const ICONS = {
  Sprout, Scissors, TreePalm, Layers, ShieldCheck, FlaskConical,
  Grid3x3, Compass, Droplets, Leaf, Lightbulb, Mountain, Droplet,
};

const HERO_IMG =
  "https://customer-assets.emergentagent.com/job_el-lawn-services/artifacts/jtgxkxiw_EL%20Lawn_banner%20image.jpeg";

export default function Home() {
  const featured = SERVICES.slice(0, 6);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section
        data-testid="home-hero"
        className="relative min-h-[88vh] flex items-center"
      >
        <img
          src={HERO_IMG}
          alt="Manicured South Florida lawn with palm trees"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 w-full">
          <div className="max-w-3xl text-white animate-fade-up">
            <span
              data-testid="hero-badge"
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/15 border border-white/30 backdrop-blur-md text-xs sm:text-sm font-medium tracking-wide"
            >
              <Award className="h-4 w-4 text-[#FCA5A5]" />
              {BRAND.experienceYears}+ Years Serving South Florida
            </span>
            <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] text-white">
              Reliable Lawn Care &amp; Beautiful Landscapes Across South Florida.
            </h1>
            <p className="mt-6 text-base sm:text-lg text-gray-100/90 max-w-2xl font-body leading-relaxed">
              Founded by Enrique Lopez, E L Lawn Services Inc has been caring
              for properties across Jupiter, Tequesta, Hobe Sound, Stuart,
              Palm Beach Gardens, and West Palm Beach for over two decades.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Button
                asChild
                size="lg"
                data-testid="hero-quote-button"
                className="bg-[#DC2626] hover:bg-red-700 text-white rounded-full px-7 h-12 text-base"
              >
                <Link to="/contact">Get a Free Quote</Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                data-testid="hero-call-button"
                className="bg-white/10 border-white/40 text-white hover:bg-white hover:text-gray-900 rounded-full px-7 h-12 text-base backdrop-blur"
              >
                <a href={`tel:${BRAND.phoneTel}`}>
                  <Phone className="mr-2 h-4 w-4" />
                  Call {BRAND.phone}
                </a>
              </Button>
            </div>

            <div className="mt-12 grid grid-cols-3 gap-6 max-w-xl">
              {[
                { icon: Award, label: `${BRAND.experienceYears}+ Yrs`, sub: "Experience" },
                { icon: ShieldHalf, label: "Insured", sub: "& Professional" },
                { icon: Clock, label: "On-Time", sub: "Every Visit" },
              ].map((s) => (
                <div key={s.label} className="flex items-center gap-3">
                  <s.icon className="h-7 w-7 text-[#86EFAC]" />
                  <div>
                    <div className="font-display text-xl font-bold leading-none">{s.label}</div>
                    <div className="text-xs text-gray-200/80">{s.sub}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES OVERVIEW */}
      <section
        data-testid="home-services-section"
        className="py-24 leaf-pattern"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <div className="section-divider mb-6" />
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-gray-900">
              Complete care for your <span className="brand-gradient-text">South Florida</span> property
            </h2>
            <p className="mt-4 text-gray-600 font-body">
              From weekly mowing to landscape design, our crews handle every
              detail so your yard always looks its best.
            </p>
          </div>

          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {featured.map((s) => {
              const Icon = ICONS[s.icon] || Leaf;
              return (
                <div
                  key={s.slug}
                  data-testid={`home-service-card-${s.slug}`}
                  className="group relative bg-white rounded-2xl border border-gray-200 overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:border-[#15803D] hover:shadow-[0_20px_40px_-20px_rgba(21,128,61,0.35)]"
                >
                  <div className="relative h-44 overflow-hidden">
                    <img
                      src={s.image}
                      alt={s.title}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/45 to-transparent" />
                    <div className="absolute top-3 left-3 h-10 w-10 rounded-xl bg-white/95 backdrop-blur grid place-items-center text-[#15803D] shadow-md">
                      <Icon className="h-5 w-5" />
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="font-display text-lg font-semibold text-gray-900">
                      {s.title}
                    </h3>
                    <p className="mt-2 text-sm text-gray-600 font-body leading-relaxed">
                      {s.short}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-12 text-center">
            <Button
              asChild
              data-testid="home-view-all-services"
              variant="outline"
              className="border-[#15803D] text-[#15803D] hover:bg-[#15803D] hover:text-white rounded-full px-6 h-11"
            >
              <Link to="/services">
                View All Services
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section data-testid="home-why-section" className="py-24 bg-[#0F1A14] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid gap-12 lg:grid-cols-2 items-center">
          <div>
            <div className="section-divider mb-6" />
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
              Why neighbors choose us, year after year
            </h2>
            <p className="mt-4 text-gray-300 font-body">
              We're a small, owner-operated team that genuinely cares about how
              your property looks. No call centers. No surprise crews. Just
              consistent, thoughtful work — week in, week out.
            </p>

            <div className="mt-10 grid sm:grid-cols-2 gap-6">
              {[
                { title: "Founder-Led", body: "Enrique is on the job. You always know who's caring for your property." },
                { title: "Local Expertise", body: "Two decades of South Florida turf, soil, and tropical plant know-how." },
                { title: "Fully Insured", body: "Licensed, insured, and equipped for residential and commercial work." },
                { title: "Reliable Schedule", body: "On-time service, clear communication, and consistent results." },
              ].map((b) => (
                <div key={b.title} className="border-l-2 border-[#DC2626] pl-4">
                  <div className="font-display text-lg font-semibold">{b.title}</div>
                  <p className="text-sm text-gray-300 mt-1 font-body">{b.body}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="relative">
            <img
              src="https://images.unsplash.com/photo-1722480419968-a8785b569190?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwyfHxiZWF1dGlmdWwlMjBmbG9yaWRhJTIwdHJvcGljYWwlMjBiYWNreWFyZCUyMGdhcmRlbnxlbnwwfHx8fDE3NzgyNjgyODh8MA&ixlib=rb-4.1.0&q=85"
              alt="Beautiful Florida tropical garden"
              className="rounded-3xl object-cover w-full h-[480px] shadow-2xl"
            />
            <div className="absolute -bottom-6 -left-6 bg-[#DC2626] text-white rounded-2xl px-6 py-5 shadow-xl hidden md:block">
              <div className="font-display text-3xl font-bold leading-none">{BRAND.experienceYears}+</div>
              <div className="text-xs uppercase tracking-wider mt-1">Years of Trust</div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICE AREAS */}
      <section data-testid="home-areas-section" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="section-divider mb-6 mx-auto" />
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-gray-900">
            Proudly serving the Treasure Coast & Palm Beaches
          </h2>
          <p className="mt-3 text-gray-600 font-body max-w-2xl mx-auto">
            If you're in any of these communities, we're ready to take great
            care of your property.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            {BRAND.serviceAreas.map((area) => (
              <span
                key={area}
                data-testid={`area-chip-${area.toLowerCase().replace(/\s+/g, "-")}`}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-gray-200 text-sm text-gray-800 font-medium"
              >
                <MapPin className="h-3.5 w-3.5 text-[#15803D]" />
                {area}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section data-testid="home-testimonials-section" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <div className="section-divider mb-6 mx-auto" />
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-gray-900">
              What our clients are saying
            </h2>
            <p className="mt-3 text-gray-600 font-body">
              Real reviews from homeowners across our service areas.
            </p>
          </div>

          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {TESTIMONIALS.map((t, i) => (
              <div
                key={i}
                data-testid={`testimonial-card-${i}`}
                className="bg-gray-50 rounded-2xl p-7 border border-gray-200 flex flex-col"
              >
                <div className="flex gap-0.5 text-[#DC2626] mb-4">
                  {Array.from({ length: t.rating }).map((_, idx) => (
                    <Star key={idx} className="h-4 w-4 fill-current" />
                  ))}
                </div>
                <p className="text-gray-800 font-body leading-relaxed flex-1">
                  "{t.quote}"
                </p>
                <div className="mt-6 pt-5 border-t border-gray-200">
                  <div className="font-display font-semibold text-gray-900">
                    {t.name}
                  </div>
                  <div className="text-xs text-gray-500">{t.location}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section
        data-testid="home-cta-section"
        className="relative py-20 bg-[#15803D] text-white overflow-hidden"
      >
        <div className="absolute -right-20 -top-20 h-80 w-80 rounded-full bg-white/5 blur-3xl" />
        <div className="absolute -left-10 -bottom-20 h-80 w-80 rounded-full bg-[#DC2626]/20 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid gap-8 md:grid-cols-2 items-center">
          <div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
              Ready for a yard that turns heads?
            </h2>
            <p className="mt-4 text-white/90 font-body max-w-lg">
              Tell us what you need — we'll respond fast with a free,
              no-pressure quote.
            </p>
          </div>
          <div className="flex flex-wrap gap-4 md:justify-end">
            <Button
              asChild
              size="lg"
              data-testid="cta-quote-button"
              className="bg-[#DC2626] hover:bg-red-700 text-white rounded-full px-7 h-12 text-base"
            >
              <Link to="/contact">Request a Quote</Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              data-testid="cta-call-button"
              className="bg-white text-[#15803D] border-white hover:bg-white/90 rounded-full px-7 h-12 text-base"
            >
              <a href={`tel:${BRAND.phoneTel}`}>
                <Phone className="mr-2 h-4 w-4" />
                {BRAND.phone}
              </a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
